import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { SplashCursor } from '../components/SplashCursor';
import { useTheme } from '../theme/ThemeContext';

let THEME = {};
const getStyles = () => StyleSheet.create({
    container: { flex: 1, backgroundColor: THEME.bg, minHeight: '100vh' },
    backgroundContainer: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0 },
    scrollContent: { paddingVertical: 60, paddingHorizontal: 20, alignItems: 'center', zIndex: 1 },
    card: {
        backgroundColor: THEME.bgCard, backdropFilter: 'blur(20px)', borderRadius: 32, padding: 48,
        width: '100%', maxWidth: 900, borderWidth: 1, borderColor: THEME.border,
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    },
    backButton: {
        marginBottom: 32, alignSelf: 'flex-start', paddingVertical: 8, paddingHorizontal: 16,
        borderRadius: 12, backgroundColor: THEME.bgSurface,
    },
    backButtonText: { color: THEME.textSecondary, fontWeight: '700', fontSize: 12, letterSpacing: 1 },
    title: { fontSize: 48, fontWeight: '800', color: THEME.textPrimary, marginBottom: 12, fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' },
    updatedDate: { fontSize: 14, color: '#3b82f6', fontWeight: '600', marginBottom: 48, letterSpacing: '0.05em', textTransform: 'uppercase' },
    content: { gap: 16 },
    section: { marginBottom: 24 },
    sectionHeader: {
        fontSize: 22, fontWeight: '700', color: THEME.textPrimary, marginBottom: 16, fontFamily: 'Outfit, sans-serif',
        backgroundColor: THEME.bgSurface, paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8, alignSelf: 'flex-start',
    },
    subSectionHeader: { fontSize: 18, fontWeight: '600', color: THEME.textPrimary, marginTop: 16, marginBottom: 12 },
    paragraph: { fontSize: 17, lineHeight: 28, color: THEME.textSecondary, marginBottom: 12, fontWeight: '400' },
    contactSection: { marginTop: 40, padding: 32, backgroundColor: 'rgba(59, 130, 246, 0.05)', borderRadius: 24, borderWidth: 1, borderColor: 'rgba(59, 130, 246, 0.1)' },
    contactTitle: { fontSize: 18, fontWeight: '800', color: THEME.textPrimary, marginBottom: 12, letterSpacing: '0.1em' },
    email: { color: '#3b82f6', fontWeight: '600' },
});
let styles = getStyles();

const TermsOfUseScreen = () => {
    const navigate = useNavigate();
    const { theme } = useTheme();
    Object.assign(THEME, theme);
    styles = getStyles();

    return (
        <View style={styles.container}>
            {/* Background animation */}
            <View style={styles.backgroundContainer}>
                <SplashCursor
                    BACK_COLOR={theme.splashBackColor}
                    COLOR_UPDATE_SPEED={1}
                    SPLAT_RADIUS={0.2}
                    DENSITY_DISSIPATION={3.5}
                />
            </View>

            <ScrollView contentContainerStyle={styles.scrollContent}>
                <View style={styles.card}>
                    <TouchableOpacity style={styles.backButton} onPress={() => navigate(-1)}>
                        <Text style={styles.backButtonText}>← BACK</Text>
                    </TouchableOpacity>

                    <Text style={styles.title}>Terms of Use</Text>
                    <Text style={styles.updatedDate}>Updated February 26th 2026</Text>

                    <View style={styles.content}>
                        <View style={styles.section}>
                            <Text style={styles.sectionHeader}>1. Terms of Use</Text>

                            <Text style={styles.subSectionHeader}>1.1 Registration and Use of the Application</Text>
                            <Text style={styles.paragraph}>
                                The use of the Application is conditional upon acceptance of the Terms of Use and Privacy Policy.
                                Users must provide accurate and truthful information, as required.
                            </Text>

                            <Text style={styles.subSectionHeader}>1.2 Usage Restrictions</Text>
                            <Text style={styles.paragraph}>
                                It is prohibited to use the Application for illegal, harmful, or privacy-violating purposes.
                                Reverse engineering, duplication, or alteration of the Application’s source code is strictly prohibited.
                            </Text>
                        </View>

                        <View style={styles.section}>
                            <Text style={styles.sectionHeader}>2. Copyright and Intellectual Property</Text>
                            <Text style={styles.paragraph}>
                                All copyrights related to content, designs, and code in the Application are owned by the Company.
                                Users are prohibited from copying, duplicating, or distributing the information without prior approval.
                            </Text>
                        </View>

                        <View style={styles.section}>
                            <Text style={styles.sectionHeader}>3. Limitation of Liability</Text>

                            <Text style={styles.subSectionHeader}>3.1 Content Responsibility</Text>
                            <Text style={styles.paragraph}>
                                The application is not responsible for content originating from the X (Twitter) network, the AI tool used to analyze tweets, or any other third-party service integrated into the system.
                            </Text>

                            <Text style={styles.subSectionHeader}>3.2 Interruptions or Malfunctions</Text>
                            <Text style={styles.paragraph}>
                                The company shall not be held liable for any indirect, incidental, or consequential damages that may arise due to technical malfunctions, temporary service outages, or inaccuracies in the provided information.
                            </Text>
                            <Text style={styles.paragraph}>
                                The client and/or user hereby waives any claims or demands regarding reasonable deviations from standards or potential omissions in data monitoring, including missing data or inaccuracies resulting from the service.
                            </Text>

                            <Text style={styles.subSectionHeader}>3.3 Improper Use</Text>
                            <Text style={styles.paragraph}>
                                Users are solely responsible for using the system in compliance with laws and regulations.
                                The Application reserves the right to block or suspend users who violate these guidelines.
                            </Text>

                            <Text style={styles.subSectionHeader}>3.4 Disclaimer of Damages</Text>
                            <Text style={styles.paragraph}>
                                The Application is not responsible for any direct or indirect damages resulting from the use of the system, including but not limited to business losses, reputational damage, delays, or service disruptions.
                            </Text>
                            <Text style={styles.paragraph}>
                                Users are solely responsible for verifying the accuracy of the information provided by the Application and exercising judgment when using it.
                            </Text>
                        </View>

                        <View style={styles.section}>
                            <Text style={styles.sectionHeader}>4. Changes to Terms</Text>
                            <Text style={styles.paragraph}>
                                The Application reserves the right to update its Terms of Use and Privacy Policy at any time, with prior notice to users.
                            </Text>
                        </View>

                        <View style={styles.section}>
                            <Text style={styles.sectionHeader}>5. Termination of Service</Text>
                            <Text style={styles.paragraph}>
                                The Application may terminate services for users found to violate terms or misuse the Application and its services.
                                In the event of service termination, the Application does not guarantee the recovery of lost data.
                            </Text>
                        </View>

                        <View style={styles.section}>
                            <Text style={styles.sectionHeader}>6. Dispute Resolution</Text>
                            <Text style={styles.paragraph}>
                                Any disputes related to the use of the Application are subject to Israeli law and will be resolved in Israeli courts.
                                Parties will attempt to resolve disputes through mediation before seeking legal action.
                            </Text>
                        </View>

                        <View style={styles.section}>
                            <Text style={styles.sectionHeader}>7. Minors</Text>
                            <Text style={styles.paragraph}>
                                The Application is intended for users aged 16 and above.
                            </Text>
                        </View>

                        <View style={styles.section}>
                            <Text style={styles.sectionHeader}>8. Support and Customer Service</Text>
                            <Text style={styles.paragraph}>
                                Support services operate from 08:00 to 20:00.
                                The Company is committed to resolving issues within 24 hours of submission.
                            </Text>
                        </View>

                        <View style={styles.contactSection}>
                            <Text style={styles.contactTitle}>CONTACT US</Text>
                            <Text style={styles.paragraph}>
                                We welcome your feedback and questions on this conditions. If you wish to contact us, please email us at <Text style={styles.email}>zohar@reactfirst.ai</Text>.
                            </Text>
                        </View>
                    </View>
                </View>
            </ScrollView>
        </View>
    );
};


export default TermsOfUseScreen;
